import React from 'react';
import './index.css';
import type { IRoofAddress } from '../../react-app-env';
import { roofTypesMap } from '../../config/config';

interface VerticalProgressBarProps {
    activeSection: string;
    roofDrafts?: IRoofAddress[];
    selectedDraft?: IRoofAddress;
    onSelectDraft?: (draft: IRoofAddress) => void;
    isDraftValid?: (draft: IRoofAddress) => boolean;
}

// Later steps (after roof configuration)
const laterSections = [
    { id: 'teaser', icon: 'fa-sun-o' },
    { id: 'offerings', icon: 'fa-bolt' },
    { id: 'contact', icon: 'fa-envelope-o' },
];

const VerticalProgressBar: React.FC<VerticalProgressBarProps> = ({
    activeSection,
    roofDrafts = [],
    selectedDraft,
    onSelectDraft,
    isDraftValid
}) => {
    const handleNavClick = (stepNumber: number) => {
        const api = (window as any).fullpage_api;
        if (api) {
            api.moveTo(stepNumber);
        }
    };

    // Navigate to a specific roof's address section
    const handleRoofClick = (draft: IRoofAddress, roofIndex: number) => {
        if (onSelectDraft) {
            onSelectDraft(draft);
        }
        // Each roof has 2 sections (address + type), so section number = roofIndex * 2 + 1
        const sectionNumber = roofIndex * 2 + 1;
        handleNavClick(sectionNumber);
    };

    // Navigate to a specific roof's substep
    const handleSubstepClick = (roofIndex: number, substep: 'address' | 'type') => {
        const baseSection = roofIndex * 2 + 1;
        const sectionNumber = substep === 'address' ? baseSection : baseSection + 1;
        handleNavClick(sectionNumber);
    };

    // Check if draft has address
    const hasAddress = (draft: IRoofAddress) => {
        if (draft?.hasCustomRoof) {
            const customRoof = draft.roofs?.find((r: any) => r.isCustom);
            if (!customRoof) return false;
            return customRoof.area > 0 && customRoof.orientation !== -1;
        }
        return draft?.roofs?.length > 0;
    };

    // Check if draft has roof type selected
    const hasRoofType = (draft: IRoofAddress) => {
        return draft?.roofType && draft.roofType !== roofTypesMap[0].type;
    };

    // Check if currently viewing a roof section
    const isOnRoofSection = (roofIndex: number) => {
        const addressId = `roof-${roofIndex + 1}-address`;
        const typeId = `roof-${roofIndex + 1}-type`;
        return activeSection === addressId || activeSection === typeId;
    };

    // Check if on address substep for a specific roof
    const isOnAddressStep = (roofIndex: number) => {
        return activeSection === `roof-${roofIndex + 1}-address`;
    };

    // Check if on type substep for a specific roof
    const isOnTypeStep = (roofIndex: number) => {
        return activeSection === `roof-${roofIndex + 1}-type`;
    };

    return (
        <div className="vertical-progress-bar">
            {/* ROOF ENTRIES - One per roof draft */}
            {roofDrafts.map((draft, index) => {
                const isSelected = selectedDraft?.id === draft.id;
                const isValid = isDraftValid ? isDraftValid(draft) : true;
                const isActiveRoof = isOnRoofSection(index);
                const draftHasAddress = hasAddress(draft);
                const draftHasRoofType = hasRoofType(draft);

                return (
                    <div key={draft.id} className="roof-nav-entry">
                        {/* Main roof bubble */}
                        <div
                            className={`roof-bubble ${isSelected ? 'selected' : ''} ${isActiveRoof ? 'active' : ''} ${isValid ? 'valid' : ''}`}
                            onClick={() => handleRoofClick(draft, index)}
                            title={`Roof ${index + 1}`}
                        >
                            <i className="fa fa-home bubble-icon-default" />
                            <i className="fa fa-check bubble-icon-done" />
                            {roofDrafts.length > 1 && <span className="roof-number">{index + 1}</span>}
                            <span className={`bubble-badge ${isValid ? 'complete' : ''}`}>
                                {isValid ? <i className="fa fa-check" /> : <i className="fa fa-circle-o" />}
                            </span>
                        </div>

                        {/* Sub-indicators: Address & Roof Type (only for selected roof) */}
                        {isSelected && (
                            <div className={`roof-substeps ${isValid ? 'valid' : ''}`}>
                                <div
                                    className={`roof-substep ${isOnAddressStep(index) ? 'active' : ''} ${draftHasAddress ? 'completed' : ''}`}
                                    onClick={() => handleSubstepClick(index, 'address')}
                                    title="Address"
                                >
                                    <i className="fa fa-map-marker substep-icon-default" />
                                    <i className="fa fa-check substep-icon-done" />
                                    <span className={`substep-badge ${draftHasAddress ? 'complete' : ''}`}>
                                        {draftHasAddress ? <i className="fa fa-check" /> : <i className="fa fa-circle-o" />}
                                    </span>
                                </div>
                                <div
                                    className={`roof-substep ${isOnTypeStep(index) ? 'active' : ''} ${draftHasRoofType ? 'completed' : ''}`}
                                    onClick={() => handleSubstepClick(index, 'type')}
                                    title="Roof Type"
                                >
                                    <i className="fa fa-th-large substep-icon-default" />
                                    <i className="fa fa-check substep-icon-done" />
                                    <span className={`substep-badge ${draftHasRoofType ? 'complete' : ''}`}>
                                        {draftHasRoofType ? <i className="fa fa-check" /> : <i className="fa fa-circle-o" />}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="step-line"></div>
                    </div>
                );
            })}

            {/* LATER SECTIONS - Teaser, Offerings, Contact */}
            {laterSections.map((section, index) => {
                // Later sections come after all roof sections
                // Each roof has 2 sections, so section number = roofDrafts.length * 2 + index + 1
                const sectionNumber = roofDrafts.length * 2 + index + 1;

                return (
                    <div
                        key={section.id}
                        className={`progress-step ${activeSection === section.id ? 'active' : ''}`}
                        onClick={() => handleNavClick(sectionNumber)}
                    >
                        <div className="step-circle">
                            <i className={`fa ${section.icon}`} />
                        </div>
                        {index < laterSections.length - 1 && <div className="step-line"></div>}
                    </div>
                );
            })}
        </div>
    );
};

export default VerticalProgressBar;
